import { EffectComposer, Bloom, SMAA, Vignette, Noise } from "@react-three/postprocessing";
import { BlendFunction } from "postprocessing";
import { useSettingsStore } from "../../app/settingsStore";

/**
 * SMAA → Bloom → Vignette → grain, per M1a, now tiered (M6).
 *
 * What gets given up, and in what order, matters more than how much. Grain and
 * vignette go first: they are atmosphere, and the game still looks like itself
 * without them. Bloom survives longest of the effects, because it is what
 * makes the glass tubes and hazard lights glow warmly — a §2 requirement
 * rather than a flourish.
 */
export function PostStack() {
  const quality = useSettingsStore((s) => s.quality);

  // At "low" the composer is not mounted at all. Disabling effects inside a
  // live EffectComposer still pays for its render targets and its extra full-
  // screen passes, and not paying for them is the entire point of this tier.
  if (quality === "low") return null;

  // Built as an array rather than with a `&&` in the JSX: EffectComposer
  // types its children as elements, and a `false` in the list is a type error
  // rather than a skipped pass.
  const effects = [
    <SMAA key="smaa" />,
    <Bloom
      key="bloom"
      intensity={0.5}
      luminanceThreshold={0.55}
      luminanceSmoothing={0.3}
      mipmapBlur
      radius={0.4}
    />,
    <Vignette
      key="vignette"
      offset={0.3}
      darkness={quality === "high" ? 0.45 : 0.3}
      blendFunction={BlendFunction.NORMAL}
    />,
  ];

  if (quality === "high") {
    effects.push(<Noise key="noise" premultiply blendFunction={BlendFunction.OVERLAY} opacity={0.18} />);
  }

  return (
    <EffectComposer multisampling={0} key={quality}>
      {effects}
    </EffectComposer>
  );
}
